import api from "@/lib/api";
import { toast } from "sonner";
window.addEventListener("beforeunload", (e) => { if (voci.some((x) => x.stato === "attesa" || x.stato === "carica")) { e.preventDefault(); e.returnValue = ""; } });

// Coda di caricamento globale: continua mentre si naviga nel CRM; 5 file in parallelo, file grandi/zip a pezzi con ripresa.
const PARALLELI_MAX = 2;
let paralleli = 2;
const CHUNK = 5 * 1024 * 1024;
const A_PEZZI = 8 * 1024 * 1024;
const TENTATIVI = 3;
const PEZZI_INSIEME = 2;
const SOSPESI = "crm-caricamenti-sospesi";
export const MB = (b) => (b >= 1024 ** 3 ? `${(b / 1024 ** 3).toFixed(2)} GB` : `${(b / 1024 ** 2).toFixed(b < 10 * 1024 ** 2 ? 1 : 0)} MB`);
const durata = (s) => (s >= 3600 ? `${Math.floor(s / 3600)} h ${Math.round((s % 3600) / 60)} min` : s >= 60 ? `${Math.round(s / 60)} min` : `${Math.max(1, Math.round(s))} s`);
const chiaveFile = (f, dest) => `${f.name}|${f.size}|${f.lastModified}|${dest}`;
const leggiSospesi = () => { try { return JSON.parse(localStorage.getItem(SOSPESI) || "[]"); } catch { return []; } };
const scriviSospesi = (l) => { localStorage.setItem(SOSPESI, JSON.stringify(l.slice(-50))); window.dispatchEvent(new Event("crm:sospesi")); };
export const caricamentiSospesi = leggiSospesi;
export const dimenticaSospeso = (k) => scriviSospesi(leggiSospesi().filter((x) => x.chiave !== k));
let voci = [];
const ascoltatori = new Set();
const emetti = () => ascoltatori.forEach((f) => f([...voci]));
export const ascoltaCoda = (f) => { ascoltatori.add(f); f([...voci]); return () => ascoltatori.delete(f); };
const agg = (id, p) => { voci = voci.map((v) => (v.id === id ? { ...v, ...p } : v)); emetti(); };
const controlli = new Map();
const sig = (v) => { const c = new AbortController(); controlli.set(v.id, c); return c.signal; };
// Toglie un file dall'elenco prima che finisca (se sta caricando, interrompe l'invio).
export const rimuovi = (id) => { controlli.get(id)?.abort(); controlli.delete(id); voci = voci.filter((v) => v.id !== id); emetti(); prossimo(); };
export const pulisciCompletati = () => { voci = voci.filter((v) => v.stato !== "fatto" && v.stato !== "errore"); emetti(); };
const attesa = (ms) => new Promise((r) => setTimeout(r, ms));

export const diSistema = (nome) => { const b = (nome || "").split("/").pop(); return !b || b.startsWith("._") || [".DS_Store", "Thumbs.db", "desktop.ini"].includes(b) || (nome || "").includes("__MACOSX/"); };
export const soloUtili = (list) => Array.from(list || []).filter((f) => !diSistema(f.webkitRelativePath || f.name));

// Legge file e intere cartelle trascinate (anche sottocartelle).
export async function fileDaDrop(dt) {
  const entries = Array.from(dt?.items || []).map((i) => i.webkitGetAsEntry?.()).filter(Boolean);
  if (!entries.length) return soloUtili(dt?.files);
  const out = [];
  const leggi = async (en, path) => {
    if (en.isFile) { const f = await new Promise((res, rej) => en.file(res, rej)); if (!diSistema(path + f.name)) { try { Object.defineProperty(f, "percorso", { value: path.replace(/\/$/, "") }); } catch { /* sola lettura */ } out.push(f); } return; }
    const r = en.createReader();
    for (;;) {
      const batch = await new Promise((res, rej) => r.readEntries(res, rej));
      if (!batch.length) break;
      for (const x of batch) await leggi(x, `${path}${en.name}/`);
    }
  };
  for (const en of entries) await leggi(en, "");
  return out;
}

const dest = (v) => (v.cartella ? { cartella: v.cartella } : { sottocartella: v.sottocartella || "" });

async function caricaFoto(v) {
  const fd = new FormData();
  fd.append("cartella", v.cartella); fd.append("files", v.file, v.nome); fd.append("chiave", v.id);
  await api.post(`/marketing/foto/${v.iid}/files`, fd, { signal: sig(v), timeout: 180000, onUploadProgress: (e) => e.total && agg(v.id, { pct: Math.min(99, Math.round((e.loaded / e.total) * 100)) }) });
}

// Invio a pezzi da 5 MB (2 alla volta, tentativi automatici). Stessa chiave = riprende dai pezzi già arrivati, anche dopo aver chiuso la pagina.
export async function inviaAPezzi(file, { dest = "", signal, onProgress } = {}) {
  const chiave = chiaveFile(file, dest);
  scriviSospesi([...leggiSospesi().filter((x) => x.chiave !== chiave), { chiave, nome: file.name, size: file.size, dest, at: Date.now() }]);
  const { data } = await api.post("/caricamenti/init", { filename: file.name, size: file.size, chiave }, { signal });
  if (data.stato === "pronto") { onProgress?.(file.size, file.size, null); return data.id; }
  const pz = data.pezzo, n = Math.ceil(file.size / pz);
  const fatti = new Set(data.ricevuti || []);
  const todo = [...Array(n).keys()].filter((i) => !fatti.has(i));
  let inviati = [...fatti].reduce((s, i) => s + Math.min(pz, file.size - i * pz), 0);
  const t0 = Date.now(), base = inviati;
  const avanza = () => { const sec = (Date.now() - t0) / 1000; const vel = sec > 1 ? (inviati - base) / sec : 0; onProgress?.(inviati, file.size, vel > 0 ? (file.size - inviati) / vel : null); };
  avanza();
  const lavora = async () => {
    while (todo.length) {
      const i = todo.shift();
      for (let t = 0; ; t += 1) {
        try { await api.put(`/caricamenti/${data.id}/${i}`, file.slice(i * pz, (i + 1) * pz), { signal, headers: { "Content-Type": "application/octet-stream" }, timeout: 180000 }); break; }
        catch (e) { const st = e?.response?.status; if (signal?.aborted || t >= 8 || st === 404 || st === 403 || st === 409) throw e; await attesa(Math.min(30000, 2000 * (t + 1))); }
      }
      inviati += Math.min(pz, file.size - i * pz); avanza();
    }
  };
  await Promise.all(Array.from({ length: Math.min(PEZZI_INSIEME, todo.length || 1) }, lavora));
  for (let t = 0; ; t += 1) {
    try { await api.post(`/caricamenti/${data.id}/fine`, {}, { signal, timeout: 300000 }); break; }
    catch (e) { if (signal?.aborted || t >= 4 || e?.response?.status === 400) throw e; await attesa(3000 * (t + 1)); }
  }
  return data.id;
}
export const chiudiSospeso = (file, dest = "") => dimenticaSospeso(chiaveFile(file, dest));
const progresso = (id) => (fatti, tot, eta) => agg(id, { pct: Math.min(99, Math.round((fatti / tot) * 100)), caricati: fatti, eta, nota: eta != null ? `${MB(fatti)} di ${MB(tot)} · mancano ${durata(eta)}` : `${MB(fatti)} di ${MB(tot)}` });

// File grande da un modulo qualsiasi (checklist, proposte, rogiti, Archivio...): compare nel pannello caricamenti e restituisce l'id da registrare.
export async function caricaGrande(file, dest = "") {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  voci.push({ id, file, nome: file.name, size: file.size, stato: "carica", pct: 0, tentativi: 0, esterno: true });
  emetti();
  try {
    const cid = await inviaAPezzi(file, { dest, signal: sig({ id }), onProgress: progresso(id) });
    agg(id, { pct: 99, nota: "Registrazione…" });
    return { cid, fine: (ok, errore) => { if (ok) chiudiSospeso(file, dest); agg(id, ok ? { stato: "fatto", pct: 100, nota: null } : { stato: "errore", errore: errore || "Caricamento non riuscito", nota: null }); } };
  } catch (e) {
    agg(id, { stato: "errore", nota: null, errore: e?.response?.data?.detail || "Connessione interrotta: riprova, riparte da dove si era fermato" });
    throw e;
  }
}

async function caricaDoc(v) {
  const fd = new FormData();
  const img = /\.(jpe?g|png|webp|heic|heif)$/i.test(v.nome) && (v.sottocartella || "").startsWith("Foto");
  const dest = `immobile:${v.iid}:${v.sottocartella || ""}`;
  if (v.size > A_PEZZI) fd.append("caricamento_id", await inviaAPezzi(v.file, { dest, signal: sig(v), onProgress: progresso(v.id) }));
  else fd.append("file", v.file, v.nome);
  fd.append("entity_type", "immobile"); fd.append("entity_id", v.iid);
  fd.append("kind", img ? "foto" : "documento"); if (!img) fd.append("doc_tipo", "altro");
  if (v.sottocartella) fd.append("sottocartella", v.sottocartella);
  fd.append("conflitto", v.conflitto || "entrambi"); fd.append("chiave", v.id);
  await api.post("/files/upload", fd, { signal: sig(v), timeout: 300000, onUploadProgress: (e) => v.size <= A_PEZZI && e.total && agg(v.id, { pct: Math.min(99, Math.round((e.loaded / e.total) * 100)) }) });
  if (v.size > A_PEZZI) chiudiSospeso(v.file, dest);
}

async function caricaPezzi(v) {
  const { file, iid } = v;
  const signal = sig(v);
  const { data } = await api.post(`/marketing/foto/${iid}/video/init`, { filename: v.nome, size: file.size, ...dest(v) }, { signal });
  const ricevuti = new Set(data.ricevuti || []);
  const n = Math.ceil(file.size / CHUNK);
  for (let i = 0; i < n; i += 1) {
    if (!ricevuti.has(i)) {
      for (let t = 0; ; t += 1) {
        try { await api.put(`/marketing/foto/${iid}/video/${data.upload_id}/${i}`, file.slice(i * CHUNK, (i + 1) * CHUNK), { signal, headers: { "Content-Type": "application/octet-stream" }, timeout: 120000 }); break; }
        catch (e) { if (signal.aborted || t >= 5 || e?.response?.status === 404 || e?.response?.status === 403) throw e; agg(v.id, { nota: "Connessione interrotta, riprendo…" }); await attesa(2000 * (t + 1)); }
      }
    }
    agg(v.id, { pct: Math.min(95, Math.round(((i + 1) / n) * 95)), nota: null });
  }
  await api.post(`/marketing/foto/${iid}/video/${data.upload_id}/fine`);
  const zip = /\.zip$/i.test(v.nome);
  agg(v.id, { nota: zip ? "Scompatto lo zip…" : "Salvataggio…" });
  for (let k = 0; k < 2400; k += 1) {
    await attesa(1500);
    let s;
    try { s = (await api.get(`/marketing/foto/${iid}/video/${data.upload_id}/stato`)).data; } catch { continue; }
    if (zip && s.totale != null) agg(v.id, { nota: `Scompatto lo zip: ${(s.estratti || 0) + (s.saltati || 0)}/${s.totale} file` });
    if (s.stato === "errore") throw Object.assign(new Error(s.errore), { response: { data: { detail: s.errore || "Elaborazione non riuscita" } }, definitivo: true });
    if (s.stato === "fatto") return zip ? `${s.estratti || 0} file estratti${s.saltati ? `, ${s.saltati} saltati (non validi o di sistema)` : ""}` : null;
  }
  throw Object.assign(new Error("timeout"), { response: { data: { detail: "Elaborazione troppo lunga: controlla la cartella e riprova" } }, definitivo: true });
}

function prossimo() {
  while (voci.filter((v) => v.stato === "carica").length < paralleli) {
    const v = voci.find((x) => x.stato === "attesa");
    if (!v) return;
    agg(v.id, { stato: "carica", errore: null });
    const grande = v.cartella && (v.video || v.zip || v.size > A_PEZZI);
    (grande ? caricaPezzi(v) : v.cartella ? caricaFoto(v) : caricaDoc(v))
      .then((nota) => {
        paralleli = Math.min(PARALLELI_MAX, paralleli + 0.25);
        agg(v.id, { stato: "fatto", pct: 100, nota: nota || null });
        window.dispatchEvent(new CustomEvent("crm:foto-caricata", { detail: { iid: v.iid } }));
        finale();
      })
      .catch(async (e) => {
        if (!voci.some((x) => x.id === v.id)) return;
        const st = e?.response?.status;
        if (st === 429) {
          agg(v.id, { stato: "pausa", nota: "Troppi caricamenti al minuto, attendo e riprendo…" });
          await attesa(15000);
          agg(v.id, { stato: "attesa" }); prossimo(); return;
        }
        const rete = !e?.response;
        if (rete) paralleli = Math.max(1, Math.floor(paralleli / 2));
        const tent = (v.tentativi || 0) + 1;
        if (!e?.definitivo && st !== 400 && st !== 403 && tent < (rete ? 6 : TENTATIVI)) {
          agg(v.id, { stato: "pausa", tentativi: tent, nota: `Non riuscito, riprovo (${tent})…` });
          await attesa(3000 * tent);
          agg(v.id, { stato: "attesa" }); prossimo(); return;
        }
        agg(v.id, { stato: "errore", tentativi: tent, nota: null, errore: e?.response?.data?.detail || (e?.message === "Network Error" ? "Connessione interrotta" : "Caricamento non riuscito") });
        finale();
      })
      .finally(prossimo);
  }
}

function finale() {
  if (voci.some((x) => ["attesa", "carica", "pausa"].includes(x.stato))) return;
  const ok = voci.filter((x) => x.stato === "fatto").length;
  const ko = voci.filter((x) => x.stato === "errore").length;
  if (ko) toast.error(`${ok} caricati, ${ko} non riusciti`, { id: "coda-esito" });
  else toast.success("Caricamento completato", { id: "coda-esito" });
}

export function accodaFile(files, { iid, cartella = null, sottocartella = null, conflitto = null }) {
  const lista = soloUtili(files);
  lista.forEach((file) => {
    const video = /\.(mp4|mov)$/i.test(file.name) || file.type.startsWith("video/");
    const zip = /\.zip$/i.test(file.name);
    const dir = file.percorso ?? (file.webkitRelativePath || "").split("/").slice(0, -1).join("/");
    const sotto = cartella ? sottocartella : [sottocartella, dir].filter(Boolean).join("/").split("/").slice(0, 5).join("/") || sottocartella;
    voci.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, file, nome: file.name, size: file.size, iid, cartella, sottocartella: sotto, conflitto, video, zip, stato: "attesa", pct: 0, tentativi: 0 });
  });
  emetti();
  prossimo();
  return lista.length;
}

export const riprova = (id) => { const v = voci.find((x) => x.id === id); if (v?.esterno) { toast.message("Ricarica lo stesso file: riparte da dove si era fermato"); return; } agg(id, { stato: "attesa", pct: 0, errore: null, tentativi: 0 }); prossimo(); };
export const riprovaNonRiusciti = () => { voci = voci.map((v) => (v.stato === "errore" && !v.esterno ? { ...v, stato: "attesa", pct: 0, errore: null, tentativi: 0 } : v)); emetti(); prossimo(); };
